import React from 'react';
import classNames from 'classnames';

import useAnalytics, { Analytics } from 'hooks/useAnalytics';
import Spinner from './Spinner';

export enum ButtonType {
  /* eslint-disable max-len */
  PRIMARY = 'bg-btn-primary-bg text-btn-primary-text border-btn-primary-size border-btn-primary-border has-hover:hover:border-btn-primary-hover-border has-hover:hover:text-btn-primary-hover-text has-hover:hover:bg-btn-primary-hover-bg disabled:text-btn-primary-disabled-text disabled:bg-btn-primary-disabled-bg disabledStyle:bg-btn-primary-disabled-bg disabledStyle:text-btn-primary-disabled-text',

  SECONDARY = 'bg-btn-secondary-bg text-btn-secondary-text border-btn-secondary-size border-btn-secondary-border has-hover:hover:bg-btn-secondary-hover-bg has-hover:hover:text-btn-secondary-hover-text has-hover:hover:border-btn-secondary-hover-border disabled:bg-btn-secondary-disabled-bg disabledStyle:bg-btn-secondary-disabled-bg disabled:border-btn-secondary-disabled-border disabled:text-btn-secondary-disabled-text disabledStyle:text-btn-secondary-disabled-text disabledStyle:border-btn-secondary-disabled-border [text-transform:var(--btn-text-transform)]',

  TERTIARY = 'bg-btn-tertiary-bg text-btn-tertiary-text has-hover:hover:bg-btn-tertiary-hover-bg disabled:text-btn-tertiary-disabled-text disabled:bg-btn-tertiary-disabled-bg has-hover:hover:disabled:bg-btn-tertiary-disabled-bg disabledStyle:bg-btn-tertiary-disabled-bg',

  WHITE = 'bg-white text-default hover:border-border-default focus:shadow-focus focus:hover:shadow-none disabled:bg-white/50 disabled:hover:text-default outline-tertiary/10 disabledStyle:bg-white/50 disabledStyle:hover:text-default outline-tertiary/10',
  DENY = 'bg-default text-white hover:default/90 focus:bg-default/80 focus:shadow-focus focus:hover:shadow-none',
  LIGHT = 'bg-transparent text-default',
  ERROR = 'bg-danger-default text-white hover:bg-btn-error-hover-bg disabled:bg-btn-error-disabled-bg disabledStyle:bg-btn-error-disabled-bg focus:ring-btn-error-disabled-bg',
  SUCCESS = 'bg-success-default text-white hover:bg-success-darken-15 disabled:bg-success-lighten-60 disabledStyle:bg-success-lighten-60',
  ICON = 'bg-transparent text-white disabled:opacity-25 disabled:hover-bg-transparent disabled:cursor-default'
  /* eslint-enable max-len */
}

export enum ButtonSize {
  SMALL = 'small',
  NORMAL = 'normal',
}

export type ButtonProps = React.PropsWithChildren<{
  type?: ButtonType;
  submit?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  'aria-label'?: string;
  testId?: string;
  disabledStyle?: boolean;
  analytics?: Analytics;
  size?: ButtonSize;
}>;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((
  {
    type = ButtonType.TERTIARY,
    submit,
    disabled: disabledProp,
    loading,
    onClick: onClickDefault,
    className,
    'aria-label': ariaLabel,
    children,
    testId,
    disabledStyle,
    analytics,
    size = ButtonSize.NORMAL,
  },
  ref,
) => {
  const { onClick } = useAnalytics();

  const disabled = disabledProp || loading;

  return (
    <button
      ref={ref}
      onClick={onClick(analytics, onClickDefault)}
      aria-label={ariaLabel}
      data-testid={testId}
      disabled={disabled}
      type={submit ? 'submit' : 'button'}
      className={classNames(
        { 'cursor-not-allowed': disabled },
        // eslint-disable-next-line max-len
        'rounded-button font-btn-weight py-4 px-7 button transform transition duration-500 p-4 kiosk:p-9 kioskSharebox:p-5 text-size-button-s drop-shadow-none',
        type,
        { 'px-s leading-m text-button-xs iconBtn:px-2xs': size === ButtonSize.SMALL },
        { 'px-l leading-l text-button-s iconBtn:px-s': size === ButtonSize.NORMAL },
        className,
      )}
      data-fake-disabled={disabledStyle}
    >
      {loading && (<Spinner className={classNames({ '!text-white/30': type === ButtonType.TERTIARY })} />)}
      {!loading && children}
    </button>
  );
});

export default Button;
